import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { RevenueLog } from '@/hooks/useSupabaseStore';

const typeLabels: Record<string, string> = {
  extend: 'Đóng lãi',
  redeem: 'Chuộc đồ',
  other: 'Khác',
};

function formatMoney(n: number) {
  return n.toLocaleString('vi-VN') + ' đ';
}

function getMonthKey(dateStr: string) {
  return dateStr.slice(0, 7);
}

function getMonthName(key: string) {
  const [y, m] = key.split('-');
  return `Tháng ${m}/${y}`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type RevenueExportFormat = 'excel' | 'pdf';

interface MonthlyReport {
  monthKey: string;
  monthName: string;
  total: number;
  extend: number;
  redeem: number;
  other: number;
  count: number;
  dayCount: number;
}

export function useExportRevenue() {
  const [exporting, setExporting] = useState(false);

  const buildMonthlyData = useCallback((logs: RevenueLog[]): MonthlyReport[] => {
    const map: Record<string, MonthlyReport> = {};
    logs.forEach((log) => {
      const key = getMonthKey(log.createdAt);
      if (!map[key]) {
        map[key] = {
          monthKey: key,
          monthName: getMonthName(key),
          total: 0,
          extend: 0,
          redeem: 0,
          other: 0,
          count: 0,
          dayCount: 0,
        };
      }
      const r = map[key];
      r.total += log.interestAmount;
      r.count += 1;
      if (log.type === 'extend') r.extend += log.interestAmount;
      else if (log.type === 'redeem') r.redeem += log.interestAmount;
      else r.other += log.interestAmount;
    });
    // Đếm số ngày có giao dịch
    const dayMap: Record<string, Set<string>> = {};
    logs.forEach((log) => {
      const mk = getMonthKey(log.createdAt);
      const dk = log.createdAt.slice(0, 10);
      if (!dayMap[mk]) dayMap[mk] = new Set();
      dayMap[mk].add(dk);
    });
    Object.keys(map).forEach((k) => {
      map[k].dayCount = dayMap[k]?.size ?? 0;
    });
    return Object.values(map).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, []);

  const exportRevenue = useCallback(
    async (logs: RevenueLog[], format: RevenueExportFormat, monthKey?: string) => {
      if (logs.length === 0) {
        alert('Không có dữ liệu doanh thu để xuất!');
        return;
      }

      setExporting(true);

      const filtered = monthKey ? logs.filter((l) => getMonthKey(l.createdAt) === monthKey) : logs;
      if (filtered.length === 0) {
        alert('Không có dữ liệu trong tháng đã chọn!');
        setExporting(false);
        return;
      }

      const monthlyData = buildMonthlyData(filtered);
      const grandTotal = monthlyData.reduce((s, m) => s + m.total, 0);
      const grandCount = monthlyData.reduce((s, m) => s + m.count, 0);
      const grandDays = monthlyData.reduce((s, m) => s + m.dayCount, 0);

      const filenameBase = monthKey
        ? `Bao_cao_doanh_thu_${monthKey}`
        : `Bao_cao_doanh_thu_tong_hop_${new Date().toISOString().slice(0, 10)}`;

      try {
        if (format === 'excel') {
          const wb = XLSX.utils.book_new();

          // Sheet 1: Tổng hợp theo tháng
          const summaryRows = monthlyData.map((m) => ({
            'Tháng': m.monthName,
            'Tổng doanh thu': m.total,
            'Đóng lãi': m.extend,
            'Chuộc đồ': m.redeem,
            'Khác': m.other,
            'Số giao dịch': m.count,
            'Số ngày có GD': m.dayCount,
            'TB / ngày': Math.round(m.total / Math.max(1, m.dayCount)),
          }));
          const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
          wsSummary['!cols'] = [
            { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
            { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
          ];
          XLSX.utils.book_append_sheet(wb, wsSummary, 'Tổng hợp tháng');

          // Sheet 2: Chi tiết từng giao dịch
          const detailRows = filtered
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((l) => ({
              'Ngày': l.createdAt.slice(0, 10),
              'Giờ': l.createdAt.slice(11, 16),
              'Loại': typeLabels[l.type] || l.type,
              'Khách hàng': l.customerName,
              'Tài sản': l.assetName,
              'Mã KV': l.loanId,
              'Số tiền': l.interestAmount,
              'Tổng phải trả': l.totalPayback,
              'Ghi chú': l.note || '',
            }));
          const wsDetail = XLSX.utils.json_to_sheet(detailRows);
          wsDetail['!cols'] = [
            { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 18 },
            { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 30 },
          ];
          XLSX.utils.book_append_sheet(wb, wsDetail, 'Chi tiết giao dịch');

          const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadBlob(blob, `${filenameBase}.xlsx`);
        } else {
          // PDF
          const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

          doc.setFontSize(16);
          doc.text(
            monthKey ? `BÁO CÁO DOANH THU - ${getMonthName(monthKey)}` : 'BÁO CÁO DOANH THU TỔNG HỢP',
            148,
            14,
            { align: 'center' }
          );
          doc.setFontSize(10);
          doc.text(`Ngày xuất: ${new Date().toLocaleDateString('vi-VN')}`, 148, 20, { align: 'center' });

          // Bảng tổng hợp tháng
          const summaryHeaders = [
            ['Tháng', 'Tổng DT', 'Đóng lãi', 'Chuộc đồ', 'Khác', 'Số GD', 'Số ngày', 'TB/ngày'],
          ];
          const summaryBody = monthlyData.map((m) => [
            m.monthName,
            formatMoney(m.total),
            formatMoney(m.extend),
            formatMoney(m.redeem),
            formatMoney(m.other),
            String(m.count),
            String(m.dayCount),
            formatMoney(Math.round(m.total / Math.max(1, m.dayCount))),
          ]);

          autoTable(doc, {
            head: summaryHeaders,
            body: summaryBody,
            startY: 26,
            styles: { fontSize: 9, cellPadding: 2 },
            headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            margin: { left: 10, right: 10 },
            theme: 'grid',
          });

          const finalY =
            (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || 80;

          // Grand total
          doc.setFontSize(12);
          doc.text('TỔNG KẾT', 10, finalY + 10);
          autoTable(doc, {
            body: [
              ['Tổng doanh thu', formatMoney(grandTotal)],
              ['Tổng giao dịch', String(grandCount)],
              ['Tổng ngày có GD', String(grandDays)],
              ['Trung bình / ngày', formatMoney(Math.round(grandTotal / Math.max(1, grandDays)))],
            ],
            startY: finalY + 14,
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold' } },
            alternateRowStyles: { fillColor: [250, 250, 250] },
            margin: { left: 10, right: 10 },
            theme: 'grid',
            head: [],
          });

          const pdfBlob = doc.output('blob');
          downloadBlob(pdfBlob, `${filenameBase}.pdf`);
        }
      } catch (err) {
        console.error('Export revenue error:', err);
        alert('Xuất báo cáo thất bại, vui lòng thử lại.');
      } finally {
        setExporting(false);
      }
    },
    [buildMonthlyData]
  );

  return { exportRevenue, exporting, buildMonthlyData };
}